/*
 * EmpiriVita delivery promise (added 9-9-2026)
 * ---------------------------------------------
 * Renders the Amazon-fed delivery window next to the Add to cart button (PDP)
 * and on each line item in the cart drawer and cart page, and mirrors the
 * window into cart attributes so the order confirmation email can repeat it.
 *
 * Source of truth: the ByteStand MCF "fast badge" estimate.
 *   PDP:  read from ByteStand's own badge (#deliveryBy, #orderBy) as it updates.
 *   Cart: fetched from ByteStand's storefront proxy (/apps/bytestand_proxy),
 *         the same request the badge makes, per line SKU and the visitor's zip.
 * Nothing in this file invents a date. When ByteStand has no estimate the UI
 * shows the shipping terms only. Any "Order within ..." cutoff ByteStand
 * supplies is carried through whenever it is present.
 *
 * PDP host: created here, right before #main-buy-section, only when the Add to
 * cart button is enabled (or with ?ev_promise_preview=1 for QA on a sold-out
 * product). Cart hosts: snippets/ev-delivery-promise.liquid.
 *
 * Reversal: delete this file, assets/ev-delivery.css and
 * snippets/ev-delivery-promise.liquid, then remove the render lines in
 * sections/cart-drawer.liquid and sections/main-cart.liquid and the two
 * ev-delivery includes in layout/theme.liquid.
 */
(function () {
  'use strict';

  var PROXY_PATH = '/apps/bytestand_proxy';
  var SHOP_DOMAIN = (window.Shopify && window.Shopify.shop) || 'bseqgp-3z.myshopify.com';
  var ZIP_KEY = 'zipCode';                 /* ByteStand's own sessionStorage key */
  var FALLBACK_ZIP = '28211';              /* ByteStand's own default when no zip is known */
  var CACHE_KEY = 'ev_delivery_v1';
  var CACHE_TTL_MS = 6 * 60 * 60 * 1000;   /* windows move daily; never reuse a stale one */
  var EXTENDED_IF_END_BEYOND_DAYS = 14;    /* end date this far out reads as a wider window */
  var EXTENDED_IF_SPAN_OVER_DAYS = 7;      /* or a window wider than a week */
  var PDP_GRACE_MS = 6000;                 /* wait this long for ByteStand before showing terms only */
  var ATTR = { estimate: 'Delivery estimate', zip: 'Delivery estimate zip', date: 'Delivery estimate date' };
  var SHOW_ZIP_IN_CART = false;            /* the zip is inferred on the PDP; keep it out of the cart line (Pat, 9-9) */

  var COPY = {
    arrives: 'Arrives {window}',
    arrivesBy: 'Arrives by {window}',
    terms: 'Free standard shipping. US addresses only.',
    termsSubscription: 'Free standard shipping on every delivery. US addresses only.',
    extended: 'A wider window than usual. You pay at checkout and we email tracking as soon as it ships.',
    noEstimateTitle: 'Free standard shipping',
    noEstimateMeta: 'US addresses only. A delivery estimate is not available right now.',
    zipSuffix: ' (to {zip})',
    orderWithin: 'Order within {cutoff} for this window.',
    orderWithinDate: 'Order within {cutoff} for this date.'
  };

  var HOST_HTML =
    '<span class="ev-delivery__icon" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" focusable="false">' +
        '<path d="M3 7h11v8H3z"></path><path d="M14 10h4l3 3v2h-7z"></path><circle cx="7" cy="17" r="1.6"></circle><circle cx="17" cy="17" r="1.6"></circle>' +
      '</svg>' +
    '</span>' +
    '<div class="ev-delivery__body"><p class="ev-delivery__title"></p><p class="ev-delivery__meta"></p></div>';

  var MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
  var MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DAY_MS = 86400000;

  /* -- Dates -- */

  function startOfDay(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }

  function isoDate(d) {
    var m = d.getMonth() + 1, day = d.getDate();
    return d.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
  }

  /* ByteStand quotes forward-looking dates without a year; a date more than 60 days behind the reference belongs to next year. */
  function dateFrom(month, day, ref) {
    var d = new Date(ref.getFullYear(), month, day);
    if (d.getTime() < ref.getTime() - 60 * DAY_MS) d = new Date(ref.getFullYear() + 1, month, day);
    return d;
  }

  /* "Standard Delivery Sep 15 - Sep 18" -> { display: "Sep 15 to Sep 18", start, end }. Returns null when the text has no date. */
  function parseWindow(raw) {
    if (!raw) return null;
    var m = /([A-Za-z]{3,9})\.?\s+(\d{1,2})(?:\s*(?:-|–|—|to)\s*(?:([A-Za-z]{3,9})\.?\s+)?(\d{1,2}))?/.exec(raw);
    if (!m) return null;
    var m1 = MONTHS[m[1].slice(0, 3).toLowerCase()];
    if (m1 === undefined) return null;
    var d1 = parseInt(m[2], 10);
    var m2 = m[3] ? MONTHS[m[3].slice(0, 3).toLowerCase()] : m1;
    var d2 = m[4] ? parseInt(m[4], 10) : d1;
    if (m2 === undefined || d1 < 1 || d1 > 31 || d2 < 1 || d2 > 31) return null;
    var today = startOfDay(new Date());
    var start = dateFrom(m1, d1, today);
    var end = dateFrom(m2, d2, start);
    if (end < start) end = new Date(end.getFullYear() + 1, end.getMonth(), end.getDate());
    var display = MONTH_NAMES[m1] + ' ' + d1;
    if (m[4]) display += ' to ' + MONTH_NAMES[m2] + ' ' + d2;
    return { raw: raw, start: start, end: end, display: display, single: !m[4] };
  }

  function isExtended(w) {
    var today = startOfDay(new Date());
    var daysToEnd = Math.round((startOfDay(w.end) - today) / DAY_MS);
    var span = Math.round((startOfDay(w.end) - startOfDay(w.start)) / DAY_MS);
    return daysToEnd > EXTENDED_IF_END_BEYOND_DAYS || span > EXTENDED_IF_SPAN_OVER_DAYS;
  }

  /* -- Rendering -- */

  function setText(el, text) { if (el && el.textContent !== text) el.textContent = text; }

  /* estimate: { raw: ByteStand delivery text, orderBy: ByteStand cutoff text } (a plain string is treated as raw). */
  function render(host, estimate, opts) {
    opts = opts || {};
    if (typeof estimate === 'string') estimate = { raw: estimate, orderBy: '' };
    var raw = (estimate && estimate.raw) ? String(estimate.raw).trim() : '';
    var cutoff = (estimate && estimate.orderBy) ? String(estimate.orderBy).trim() : '';
    var w = parseWindow(raw);
    var subscription = !!opts.subscription;
    var extended = false, title, meta;
    if (w) {
      extended = isExtended(w);
      var windowText = w.display + (SHOW_ZIP_IN_CART && opts.zip ? COPY.zipSuffix.replace('{zip}', opts.zip) : '');
      title = (w.single ? COPY.arrivesBy : COPY.arrives).replace('{window}', windowText);
      meta = (cutoff ? (w.single ? COPY.orderWithinDate : COPY.orderWithin).replace('{cutoff}', cutoff) + ' ' : '') +
        (extended ? COPY.extended + ' ' : '') +
        (subscription ? COPY.termsSubscription : COPY.terms);
    } else if (raw) {
      title = raw; /* real ByteStand text we could not parse: show it as is rather than guess */
      meta = (cutoff ? COPY.orderWithin.replace('{cutoff}', cutoff) + ' ' : '') + (subscription ? COPY.termsSubscription : COPY.terms);
    } else {
      title = COPY.noEstimateTitle;
      meta = COPY.noEstimateMeta;
    }
    setText(host.querySelector('.ev-delivery__title'), title);
    setText(host.querySelector('.ev-delivery__meta'), meta);
    host.classList.toggle('ev-delivery--extended', extended);
    host.classList.toggle('ev-delivery--no-estimate', !w && !raw);
    host.setAttribute('data-ev-window', w ? w.display : '');
    if (host.hidden) host.hidden = false;
    return w;
  }

  function createHost(kind, attrs) {
    var el = document.createElement('div');
    el.className = 'ev-delivery ev-delivery--' + kind;
    el.setAttribute(kind === 'pdp' ? 'data-ev-delivery-pdp' : 'data-ev-delivery-line', '');
    Object.keys(attrs).forEach(function (k) { el.setAttribute(k, attrs[k]); });
    el.setAttribute('aria-live', 'polite');
    el.hidden = true;
    el.innerHTML = HOST_HTML;
    return el;
  }

  /* -- Estimate cache (sessionStorage, keyed by SKU|zip) -- */

  function cacheRead() { try { return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}') || {}; } catch (e) { return {}; } }

  function cacheGet(sku, zip) {
    var entry = cacheRead()[sku + '|' + zip];
    return entry && typeof entry.raw === 'string' && (Date.now() - entry.ts) < CACHE_TTL_MS ? { raw: entry.raw, orderBy: entry.orderBy || '' } : null;
  }

  function cacheSet(sku, zip, raw, orderBy) {
    try {
      var all = cacheRead();
      all[sku + '|' + zip] = { raw: raw, orderBy: orderBy || '', ts: Date.now() };
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(all));
    } catch (e) {}
  }

  /* -- ByteStand proxy (same request the fast badge makes) -- */

  var inflight = {};

  function fetchEstimate(sku, zip) {
    var key = sku + '|' + zip;
    var cached = cacheGet(sku, zip);
    if (cached) return Promise.resolve(cached);
    if (inflight[key]) return inflight[key];
    inflight[key] = fetch(PROXY_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shopify_domain: SHOP_DOMAIN, sku: sku, zipcode: zip, estimated_delivery_date: '', order_by: '', type: 'fast_badge_delivery', country_code: 'USA' })
    })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (!(j && typeof j.delivery_by === 'string' && j.delivery_by)) return null;
        return { raw: j.delivery_by, orderBy: (typeof j.expires_at === 'string') ? j.expires_at : '' };
      })
      .catch(function () { return null; })
      .then(function (est) {
        delete inflight[key];
        if (est) cacheSet(sku, zip, est.raw, est.orderBy);
        return est;
      });
    return inflight[key];
  }

  /* -- Zip resolution: ByteStand's stored zip, else a first-visit IP lookup (as the PDP already does), else ByteStand's default -- */

  var zipPromise = null;

  function storedZip() {
    try { var z = sessionStorage.getItem(ZIP_KEY); return z && /^\d{5}$/.test(z) ? z : ''; } catch (e) { return ''; }
  }

  function resolveZip() {
    var z = storedZip();
    if (z) return Promise.resolve(z);
    if (zipPromise) return zipPromise;
    zipPromise = new Promise(function (resolve) {
      var done = false;
      var ctrl = ('AbortController' in window) ? new AbortController() : null;
      var timer = setTimeout(function () { if (ctrl) ctrl.abort(); finish(FALLBACK_ZIP); }, 4000);
      function finish(zip) { if (done) return; done = true; clearTimeout(timer); resolve(zip); }
      fetch('https://ipapi.co/json/', ctrl ? { signal: ctrl.signal } : {})
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var postal = d && d.postal;
          if (typeof postal === 'string' && /^\d{5}$/.test(postal)) {
            try { sessionStorage.setItem(ZIP_KEY, postal); } catch (e) {}
            finish(postal);
          } else {
            finish(FALLBACK_ZIP);
          }
        })
        .catch(function () { finish(FALLBACK_ZIP); });
    });
    return zipPromise;
  }

  /* -- Cart attributes (lets the order confirmation email repeat the window) -- */

  var lastSignature = null;

  function cartRoot() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }

  function syncCartAttributes(results, zip) {
    var parts = [], seen = {};
    results.forEach(function (r) {
      if (!(r && r.w)) return;
      var part = (r.title ? r.title + ': ' : '') + r.w.display;
      if (seen[part]) return; /* the cart page and the hidden drawer can both hold the same line */
      seen[part] = true;
      parts.push(part);
    });
    var summary = parts.join('; ');
    var quoted = parts.length ? isoDate(new Date()) : '';
    var signature = summary + '|' + (parts.length ? zip : '') + '|' + quoted;
    if (signature === lastSignature) return;
    lastSignature = signature;
    /* FormData on purpose: Appstle intercepts JSON cart requests. */
    var fd = new FormData();
    fd.append('attributes[' + ATTR.estimate + ']', summary);
    fd.append('attributes[' + ATTR.zip + ']', parts.length ? zip : '');
    fd.append('attributes[' + ATTR.date + ']', quoted);
    fetch(cartRoot() + 'cart/update.js', { method: 'POST', body: fd, headers: { Accept: 'application/json' } })
      .catch(function () { lastSignature = null; });
  }

  /* -- Cart lines (drawer + cart page) -- */

  var cartTimer = null;

  function renderCartLines() {
    var hosts = Array.prototype.slice.call(document.querySelectorAll('[data-ev-delivery-line]'));
    if (!hosts.length) return;
    resolveZip().then(function (zip) {
      var jobs = hosts.map(function (host) {
        var sku = host.getAttribute('data-sku') || '';
        var opts = { subscription: host.getAttribute('data-subscription') === 'true', zip: zip };
        var title = host.getAttribute('data-line-title') || '';
        if (!sku) { render(host, '', opts); return Promise.resolve({ host: host, title: title, w: null }); }
        var cached = cacheGet(sku, zip);
        if (cached) return Promise.resolve({ host: host, title: title, w: render(host, cached, opts) });
        return fetchEstimate(sku, zip).then(function (est) {
          if (!document.body.contains(host)) return null; /* drawer re-rendered meanwhile */
          return { host: host, title: title, w: render(host, est || '', opts) };
        });
      });
      Promise.all(jobs).then(function (results) { syncCartAttributes(results.filter(Boolean), zip); });
    });
  }

  function scheduleCart() {
    clearTimeout(cartTimer);
    cartTimer = setTimeout(renderCartLines, 150);
  }

  function initCart() {
    if (!document.body) return;
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 && (n.matches('[data-ev-delivery-line]') || n.querySelector('[data-ev-delivery-line]'))) { scheduleCart(); return; }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
    ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function (name) {
      document.addEventListener(name, scheduleCart);
    });
    scheduleCart();
  }

  /* -- PDP buy box -- */

  function productMeta() {
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    return meta && meta.product ? meta.product : null;
  }

  function initPdp() {
    var section = document.getElementById('main-buy-section');
    if (!section || !section.parentNode) return;
    var form = section.querySelector('form');
    var button = form && form.querySelector('button[name="add"]');
    var idInput = form && form.querySelector('input[name="id"]');
    if (!form || !button || !idInput) return;
    if (button.hasAttribute('data-native-pre-order-btn')) return; /* pre-order posture: PreProduct owns this space */
    var purchasable = !button.disabled && button.getAttribute('aria-disabled') !== 'true';
    var qaPreview = /(?:\?|&)ev_promise_preview=1(?:&|$)/.test(location.search);
    if (!purchasable && !qaPreview) return; /* sold out: the Notify me button owns this space */

    var variantId = idInput.value;
    var meta = productMeta();
    var variant = null;
    if (meta && meta.variants) {
      for (var i = 0; i < meta.variants.length; i++) { if (String(meta.variants[i].id) === String(variantId)) { variant = meta.variants[i]; break; } }
    }
    var sku = (variant && variant.sku) || '';
    var titleEl = document.querySelector('.product-title-text');
    var title = ((titleEl && titleEl.textContent) || (variant && variant.name) || '').split('|')[0].trim();
    var host = createHost('pdp', { 'data-sku': sku, 'data-variant-id': variantId, 'data-line-title': title });
    section.parentNode.insertBefore(host, section);

    var badge = document.getElementById('bytestand-app-block');
    var deliveryBy = document.getElementById('deliveryBy');
    var orderBy = document.getElementById('orderBy');
    var graceTimer = null;

    function subscriptionSelected() {
      var checked = document.querySelector('input[name="selling_plan"]:checked');
      return !!(checked && checked.value);
    }

    function badgeHidden() {
      return !badge || !deliveryBy || badge.style.display === 'none' || deliveryBy.style.display === 'none';
    }

    function liveRaw() { return badgeHidden() ? '' : (deliveryBy.textContent || '').trim(); }

    function liveCutoff() { return (orderBy && !badgeHidden()) ? (orderBy.textContent || '').trim() : ''; }

    function update(force) {
      var raw = liveRaw();
      var zip = storedZip();
      if (raw) {
        clearTimeout(graceTimer);
        var cutoff = liveCutoff();
        render(host, { raw: raw, orderBy: cutoff }, { subscription: subscriptionSelected() });
        if (sku && zip) cacheSet(sku, zip, raw, cutoff);
        if (badge) badge.classList.add('ev-mirrored');
        return;
      }
      if (deliveryBy && deliveryBy.style.display === 'none') {
        /* ByteStand explicitly has nothing for this zip (invalid zip or an Amazon error). */
        clearTimeout(graceTimer);
        render(host, '', { subscription: subscriptionSelected() });
        if (badge) badge.classList.remove('ev-mirrored');
        return;
      }
      var cached = sku && zip ? cacheGet(sku, zip) : null;
      if (cached) {
        render(host, cached, { subscription: subscriptionSelected() });
        return;
      }
      if (force) {
        render(host, '', { subscription: subscriptionSelected() });
        if (badge) badge.classList.remove('ev-mirrored');
      }
    }

    if (deliveryBy) {
      new MutationObserver(function () { update(false); })
        .observe(deliveryBy, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    }
    if (orderBy) {
      new MutationObserver(function () { update(false); }).observe(orderBy, { childList: true, characterData: true, subtree: true });
    }
    if (badge) {
      new MutationObserver(function () { update(false); }).observe(badge, { attributes: true, attributeFilter: ['style'] });
    }
    document.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'selling_plan') update(false);
    });
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.appstle_subscription_wrapper')) setTimeout(function () { update(false); }, 0);
    });
    graceTimer = setTimeout(function () { update(true); }, PDP_GRACE_MS);
    update(false);
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn); else fn();
  }

  /* QA hook (read-only helpers; safe to leave in place) */
  window.EVDelivery = { parseWindow: parseWindow, render: render, fetchEstimate: fetchEstimate, renderCartLines: renderCartLines };

  ready(function () {
    try { initPdp(); } catch (e) { if (window.console) console.error('[EmpiriVita] delivery promise (PDP):', e); }
    try { initCart(); } catch (e) { if (window.console) console.error('[EmpiriVita] delivery promise (cart):', e); }
  });
})();
